import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import Table from 'react-bootstrap/Table';
import { db } from '../firebase.js';
import { formatDate } from '../common/formatDate.js';
import { compareGames } from '../common/compareGames.js';
import GameTypeBadge from './GameTypeBadge.js';

export default function Player() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [games, setGames] = useState(null);
  const [opponentNames, setOpponentNames] = useState({});

  useEffect(() => {
    getDoc(doc(db, 'players', playerId)).then(snap => {
      setPlayer(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });

    Promise.all([
      getDocs(query(collection(db, 'games'), where('player1Id', '==', playerId))),
      getDocs(query(collection(db, 'games'), where('player2Id', '==', playerId))),
    ]).then(async ([snap1, snap2]) => {
      const all = [
        ...snap1.docs.map(d => ({ id: d.id, ...d.data() })),
        ...snap2.docs.map(d => ({ id: d.id, ...d.data() })),
      ];
      all.sort(compareGames);

      const opponentIds = [...new Set(all.map(g => (g.player1Id === playerId ? g.player2Id : g.player1Id)))];
      const opponentDocs = await Promise.all(opponentIds.map(id => getDoc(doc(db, 'players', id))));
      const names = {};
      opponentDocs.forEach(snap => {
        if (snap.exists()) names[snap.id] = snap.data().name;
      });

      setOpponentNames(names);
      setGames(all);
    });
  }, [playerId]);

  if (player === null || games === null) return <p>Loading…</p>;
  if (!player) return <p>Player not found.</p>;

  const avgFor = player.gamesPlayed ? (player.totalPointsFor / player.gamesPlayed).toFixed(1) : '—';
  const avgAgainst = player.gamesPlayed ? (player.totalPointsAgainst / player.gamesPlayed).toFixed(1) : '—';

  return (
    <div className="panel">
      <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
        <h2 className="mb-0">{player.name}</h2>
        <span className="text-muted" style={{ fontSize: '1.15rem' }}>@{player.username}</span>
      </div>
      <p className="mb-1">
        {player.wins}-{player.losses}-{player.ties}
      </p>
      <p>
        <span className="d-inline d-md-none">Avg Pts Scored</span>
        <span className="d-none d-md-inline">Average Points Scored</span>
        : {avgFor} &nbsp;|&nbsp;
        <span className="d-inline d-md-none">Avg Pts Allowed</span>
        <span className="d-none d-md-inline">Average Points Allowed</span>
        : {avgAgainst}
      </p>

      <Table striped bordered hover className="mb-0">
        <thead>
          <tr>
            <th>Details</th>
            <th>Opponent</th>
            <th>Score</th>
          </tr>
        </thead>
        <tbody>
          {games.map(g => {
            const isPlayer1 = g.player1Id === playerId;
            const myScore = isPlayer1 ? g.player1Score : g.player2Score;
            const oppScore = isPlayer1 ? g.player2Score : g.player1Score;
            const oppId = isPlayer1 ? g.player2Id : g.player1Id;
            const result = myScore > oppScore ? 'W' : myScore < oppScore ? 'L' : 'T';
            return (
              <tr key={g.id} onClick={() => navigate(`/games/${g.id}`)} style={{ cursor: 'pointer' }}>
                <td>
                  <div className="d-flex flex-column flex-md-row align-items-md-center" style={{ gap: '0.5rem' }}>
                    <span className="small">{formatDate(g.datePlayed)}</span>
                    <GameTypeBadge type={g.type} />
                  </div>
                </td>
                <td>{opponentNames[oppId] ?? ''}</td>
                <td>{result} {myScore} - {oppScore}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}
